/**
 * Single source of truth for the active org's live Stripe subscription in the
 * UI, backing `useTrialStatus`.
 *
 * Fetches `GET /api/billing/subscription?org_id=...` once and derives the trial
 * state (and a plan display name) from it. This endpoint is gated on
 * `organization:view_billing`, so it only resolves for owners/admins; the plan
 * badge therefore reads the org-scoped `org_billing` row via `usePlanBadge`
 * instead, which every member can read through RLS.
 *
 * Refetches whenever the active org changes OR `OrgProvider.billingRefreshKey`
 * bumps (trial activated, extended, or the post-onboarding splash dismissed).
 * Errors are captured to PostHog and treated as "free tier, no trial" so the
 * UI quietly falls back to the normal plan view rather than flashing an error.
 *
 * The pure trial derivation lives in `lib/trialStatus` so it stays unit
 * testable without the Supabase dependency chain.
 *
 * @module lib/useSubscription
 */
import { useEffect, useMemo, useState } from 'react';
import { useSupabase } from './SupabaseProvider';
import { useOrg } from './OrgProvider';
import { getPlanDisplayName, type SubscriptionDetails } from './billing';
import { deriveTrialStatus, type TrialStatus } from './trialStatus';
import { captureException } from './telemetry/posthog/frontend';

/** Subscription payload with the optional id/price fields the endpoint adds. */
export interface SubscriptionResponse extends SubscriptionDetails {
  /** Stripe subscription id, when a subscription exists. */
  subscription_id?: string;
  /** Stripe price id, when a subscription exists. */
  price_id?: string;
}

/** Return shape of {@link useSubscription}: the raw subscription, loading flag,
 *  derived plan name, and the spread trial-status fields. */
export interface UseSubscriptionReturn extends TrialStatus {
  /** The live subscription, or null for the free tier / no org / error. */
  subscription: SubscriptionResponse | null;
  /** True while the subscription fetch is in flight. */
  loading: boolean;
  /** Plan display name (Free/Pro/Business), or null until the first load resolves. */
  planName: string | null;
}

/**
 * Fetch the active org's subscription once and derive plan name + trial state.
 *
 * @returns Subscription, loading flag, plan name, and trial-status fields.
 */
export function useSubscription(): UseSubscriptionReturn {
  const { supabase } = useSupabase();
  const { activeOrg, billingRefreshKey } = useOrg();
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [planName, setPlanName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const orgId = activeOrg?.id;

  useEffect(() => {
    let cancelled = false;

    // Synchronous reset: immediately enter loading state when dependencies change.
    // This prevents the badge from showing stale data from the previous org
    // between the effect firing and the async fetch resolving.
    setLoading(true);
    setPlanName(null);
    setSubscription(null);

    const load = async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || !orgId) {
          if (!cancelled) {
            setSubscription(null);
            setPlanName('Free');
            setLoading(false);
          }
          return;
        }

        const res = await fetch(
          `/api/billing/subscription?org_id=${encodeURIComponent(orgId)}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } },
        );

        if (cancelled) return;

        if (res.ok) {
          const data = (await res.json()) as SubscriptionResponse;
          // The endpoint returns { status: 'none' } for the free tier. Treat
          // that as "no subscription" so trial derivation produces a clean
          // zero state and the badge shows "Free".
          if (data.status === 'none') {
            setSubscription(null);
            setPlanName('Free');
          } else {
            setSubscription(data);
            setPlanName(getPlanDisplayName(data.product_id));
          }
        } else {
          captureException(
            new Error(`Subscription fetch failed: ${res.status}`),
            'useSubscription.load',
            { orgId },
          );
          setSubscription(null);
          setPlanName('Free');
        }
        setLoading(false);
      } catch (err) {
        captureException(err instanceof Error ? err : String(err), 'useSubscription.load', { orgId });
        if (!cancelled) {
          setSubscription(null);
          setPlanName('Free');
          setLoading(false);
        }
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [supabase, orgId, billingRefreshKey]);

  const derived = useMemo(
    () => deriveTrialStatus(subscription, Math.floor(Date.now() / 1000)),
    [subscription],
  );

  return { subscription, loading, planName, ...derived };
}
